import React from 'react';
import { browserPushService, type BrowserPushStatus } from '../services/browserPushService';

const DEFAULT_STATUS: BrowserPushStatus = {
  supported: false,
  secureContext: false,
  configured: false,
  permission: 'unsupported',
  subscribed: false,
  endpoint: null,
};

export const useBrowserPush = (userId?: string | null) => {
  const [status, setStatus] = React.useState<BrowserPushStatus>(DEFAULT_STATUS);
  const [loading, setLoading] = React.useState(true);
  const [updating, setUpdating] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const refresh = React.useCallback(async () => {
    if (!userId) {
      setStatus(DEFAULT_STATUS);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const nextStatus = await browserPushService.getStatus(userId);
      setStatus(nextStatus);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load browser notification status.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  const subscribe = React.useCallback(async () => {
    if (!userId) return;
    setUpdating(true);
    try {
      await browserPushService.subscribe(userId);
      const nextStatus = await browserPushService.getStatus(userId);
      setStatus(nextStatus);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to subscribe to browser notifications.');
    } finally {
      setUpdating(false);
    }
  }, [userId]);

  const unsubscribe = React.useCallback(async () => {
    setUpdating(true);
    try {
      await browserPushService.unsubscribe();
      const nextStatus = userId ? await browserPushService.getStatus(userId) : DEFAULT_STATUS;
      setStatus(nextStatus);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unsubscribe from browser notifications.');
    } finally {
      setUpdating(false);
    }
  }, [userId]);

  return {
    status,
    loading,
    updating,
    error,
    refresh,
    subscribe,
    unsubscribe,
  };
};
